import { Injectable, signal } from '@angular/core';
import mqtt from 'mqtt';
import { WeightStationMessage } from '../models/mqtt/weight-station-message';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MqttService {
  private client: mqtt.MqttClient | undefined;
  public isConnected = signal(false);

  // Replace with your actual broker details
  private readonly brokerUrl = 'wss://424f95db569043a483bd0d6c19473863.s1.eu.hivemq.cloud:8884/mqtt';
  private readonly username = 'TESTER';
  private readonly password = 'Password1';

  constructor() {
    this.connect();
  }

  private connect() {
    // Create MQTT client
    this.client = mqtt.connect(this.brokerUrl, {
      username: this.username,
      password: this.password,
      clientId: 'angular-client-' + Math.random().toString(16).substring(2, 8), // unique client ID
    });

    // Handle connection events
    this.client.on('connect', () => {
      console.log('Connected to HiveMQ Cloud');
      this.isConnected.set(true);
      // No default subscription here; child components will subscribe individually
    });

    this.client.on('error', (error) => {
      console.error('Connection error:', error);
    });

    this.client.on('close', () => {
      console.log('Connection closed');
    });
  }

  /**
   * Subscribe to a topic, returning an Observable that
   * emits `WeightStationMessage` each time a message arrives.
   */
  public subscribe(topic: string): Observable<WeightStationMessage> {
    if (!this.client) {
      console.error('MQTT client is not connected');
      // Return an empty Observable so code doesn't break
      return new Observable<WeightStationMessage>();
    }

    this.client.on('reconnect', () => {
      console.log('Connected to HiveMQ Cloud from child');
    });

    // Actually request subscription from the broker
    this.client.subscribe(topic, (err) => {
      if (err) {
        console.error(`Subscription error for topic "${topic}":`, err);
      } else {
        console.log(`Subscribed to topic "${topic}"`);
      }
    });

    // Return an observable that listens for messages on this topic
    return new Observable<WeightStationMessage>((observer) => {
      // Message callback: only forward messages that match the subscribed topic
      const messageCallback = (recvTopic: string, message: Buffer) => {
        if (recvTopic === topic) {
          try {
            const parsed: WeightStationMessage = JSON.parse(message.toString());
            observer.next(parsed);
          } catch (err) {
            observer.error(`Failed to parse JSON: ${err}`);
          }
        }
      };

      this.client?.on('message', messageCallback);

      // Cleanup when unsubscribed
      return () => {
        // Unsubscribe from the broker
        this.client?.unsubscribe(topic, (err) => {
          if (err) {
            console.error(`Unsubscribe error for topic "${topic}":`, err);
          } else {
            console.log(`Unsubscribed from topic "${topic}"`);
          }
        });

        // Remove this particular callback
        this.client?.removeListener('message', messageCallback);
      };
    });
  }

  /**
   * Publish a message to the broker
   */
  public publish(topic: string, message: string) {
    if (!this.client) {
      console.error('MQTT client is not connected');
      return;
    }
    this.client.publish(topic, message, (err) => {
      if (err) {
        console.error('Publish error:', err);
      } else {
        console.log(`Message published to topic ${topic}: ${message}`);
      }
    });
  }

  /**
   * Disconnect the client
   */
  public disconnect() {
    if (!this.client) {
      console.error('MQTT client is not connected');
      return;
    }
    this.client.end();
  }
}
